import { CSSProperties } from 'react';

import { CalendarDateTimeRange } from '~src/utils/date-time-schema';

import { useTranslation } from './use-translation';

interface UseLocalizedDateOptions {
  options?: Intl.DateTimeFormatOptions;
  time?: boolean;
  short?: boolean;
}

type Props = UseLocalizedDateOptions & {
  size?: 'sm' | 'md';
  style?: CSSProperties;
  inline?: boolean;
  dateTime: string;
};

export function useLocalizedDate(options?: UseLocalizedDateOptions) {
  const { i18n } = useTranslation();

  return function (dateTime: string) {
    return new Date(dateTime).toLocaleDateString(i18n.resolvedLanguage, {
      day: options?.short ? '2-digit' : 'numeric',
      month: options?.short ? '2-digit' : 'short',
      year: options?.short ? '2-digit' : 'numeric',
      ...(options?.time && {
        hour: 'numeric',
        minute: 'numeric',
      }),
      ...options,
    });
  };
}

export function useLocalizedDateRange(options?: UseLocalizedDateOptions) {
  const localize = useLocalizedDate(options);

  return function (range: CalendarDateTimeRange) {
    return `${localize(range.start)} - ${localize(range.end)}`;
  };
}

export function LocalizedDate(props: Props) {
  const { dateTime, options, short, time, size = 'md', inline = false } = props;
  const localize = useLocalizedDate({ options, short, time });

  const style: CSSProperties = {
    lineHeight: size === 'md' ? '1.25rem' : '1rem',
    display: inline ? 'inline' : 'block',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    ...props.style,
  };

  return (
    <time style={style} dateTime={dateTime}>
      {localize(dateTime)}
    </time>
  );
}
